import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Alert, AlertDescription } from '../../components/ui/alert'
import ImageWithFallback from '../../components/ui/ImageWithFallback'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const { resetPassword } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm()

  const onSubmit = async (data) => {
    setIsLoading(true)
    setSuccessMessage('')
    try {
      const result = await resetPassword(data.email)

      if (result.success) {
        setSuccessMessage('Password reset email sent! Check your inbox for the next steps.')
      } else {
        setError('root', { message: result.error })
      }
    } catch (error) {
      setError('root', { message: error.message || 'An unexpected error occurred' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center aurora-bg py-12 px-4 sm:px-6 lg:px-8 relative">
      <div className="absolute inset-0 golden-mesh opacity-30"></div>
      <div className="max-w-md w-full relative z-10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <ImageWithFallback
                src="/images/zova_labs_right.png"
                alt="ZovaLabs Logo"
                className="w-16 h-16 object-contain animate-gold-pulse"
              />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full animate-sparkle"></div>
            </div>
          </div>
          <h1 className="text-4xl font-display font-bold text-foreground mb-2">Forgot Password</h1>
          <p className="text-muted-foreground">
            Enter your email and we will send you instructions to reset your password.
          </p>
        </div>

        <Card variant="luxury" className="shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-display text-center">Reset your password</CardTitle>
            <CardDescription className="text-center">We will send a password reset link to your email.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="john.doe@example.com"
                  className={errors.email ? 'border-destructive' : ''}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              {errors.root && (
                <Alert variant="destructive">
                  <AlertDescription>{errors.root.message}</AlertDescription>
                </Alert>
              )}

              {successMessage && (
                <Alert>
                  <AlertDescription>{successMessage}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" variant="gold" size="lg" className="w-full shadow-2xl" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" color="text-primary-foreground" />
                    <span className="ml-2">Sending reset email...</span>
                  </>
                ) : (
                  'Send reset link'
                )}
              </Button>

              <div className="text-center pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Remembered your password?{' '}
                  <Button variant="link" asChild className="p-0 h-auto font-medium text-primary hover:text-primary/80">
                    <Link to="/login">Sign in</Link>
                  </Button>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
